<template>
  <div>
    <validation-observer ref="accountNumbers" v-slot="{ failed }">
      <div class="p-3 row" :class="[bgColor]">
        <div class="col-12 col-sm mb-4">
          <div class="label mb-2">Tipo de Cifra:</div>
          <validation-provider
            v-if="!isReadOnly"
            v-slot="{ errors }"
            :name="`accountNumber-accountNumber-${Math.random()}`"
            :rules="`${required || hasValidate ? 'required' : ''}`"
          >
            <custom-drop-down
              :id="`isFederal`"
              v-model="accountType"
              custom-error-msg="El tipo de cifra es requerido."
              :error="errors[0]"
              fieldkey="id"
              fieldname="name"
              :options="accountTypes"
              placeholder="Seleccione"
            />
          </validation-provider>
          <div v-else>{{ value.accountTypeName }}</div>
        </div>

        <div v-if="isFederal || isReadOnly" class="col-12 col-sm mb-4">
          <div class="label mb-2">Grupo de Edad:</div>
          <validation-provider
            v-if="!isReadOnly"
            v-slot="{ errors }"
            :name="`accountNumber-accountNumber-${Math.random()}`"
            :rules="`${required || hasValidate ? 'required' : ''}`"
          >
            <custom-drop-down
              :id="`billingServiceCategory`"
              v-model="billingServiceCategory"
              custom-error-msg="El grupo de edad es requerido."
              :error="errors[0]"
              fieldkey="id"
              fieldname="name"
              :options="billingServiceCategories"
              placeholder="Seleccione"
            />
          </validation-provider>
          <div v-else>
            {{ isFederal ? value.billingServiceCategoryName : 'N/A' }}
          </div>
        </div>

        <div class="col-12 col-sm mb-4">
          <div class="label mb-2">Cifra de Cuenta:</div>
          <validation-provider
            v-if="!isReadOnly"
            v-slot="{ errors }"
            :name="`accountNumber-accountNumber-${Math.random()}`"
            :rules="`${required || hasValidate ? 'required' : ''}`"
          >
            <custom-drop-down
              :id="`reason`"
              v-model="accountNumber"
              custom-error-msg="La cifra de cuenta es requerida."
              :error="errors[0]"
              fieldkey="purchaseOrderNumberId"
              fieldname="fullNumber"
              :options="filteredAccountNumbers"
              placeholder="Seleccione"
            />
          </validation-provider>
          <div v-else>{{ value.fullNumber }}</div>
        </div>

        <div class="col-12 col-md-2 mb-4">
          <div class="label mb-2">Monto:</div>
          <validation-provider
            v-if="!isReadOnly"
            v-slot="{ errors }"
            class="input"
            :name="`accountNumber-amount`"
            :rules="
              `${
                hasValidate || required ? 'required|' : ''
              }money|differentFromZero|greaterThanZero`
            "
          >
            <custom-input
              v-model="amount"
              custom-error-msg="El monto es requerido."
              :error="errors[0]"
              mask-type="Money"
            />
          </validation-provider>
          <div v-else>{{ value.amount | price }}</div>
        </div>

        <div
          v-if="isReadOnly && canRemove"
          class="col-md-2 mb-4 d-md-flex align-items-center justify-content-md-end"
        >
          <action-button
            class="outline-danger py-1 custom-action-button"
            i="fa fa-times-circle"
            @click="remove"
          />
        </div>

        <div
          v-if="!isReadOnly"
          class="row col-12 col-md-12 d-md-flex align-items-center justify-content-md-end p-0"
          :class="failed ? 'mb-4' : ''"
        >
          <div class="col-6 col-md-2">
            <action-button
              class="outline-danger py-1 w-100"
              i="fa fa-times-circle"
              @click="cancel"
            >
              Cancelar
            </action-button>
          </div>
          <div class="col-6 col-md-2 p-0">
            <action-button
              class="success py-1 w-100"
              i="fas fa-file-plus"
              @click="add"
            >
              Añadir
            </action-button>
          </div>
        </div>
      </div>
    </validation-observer>
  </div>
</template>

<script>
import CustomDropDown from 'custom-drop-down';
import CustomInput from 'CustomInput';

const accountTypeValues = Object.freeze({
  Federal: 1,
  Estatal: 2,
});

export default {
  name: 'AccountNumbersV2',
  components: {
    CustomDropDown,
    CustomInput,
  },
  props: {
    dateFrom: {
      type: Date,
      default: () => null,
    },
    dateTo: {
      type: Date,
      default: () => null,
    },
    accountNumbers: {
      type: Array,
      default: () => [],
    },
    isReadOnly: {
      type: Boolean,
      default: () => false,
    },
    value: {
      type: Object,
      default: () => {},
    },
    required: {
      type: Boolean,
      default: () => true,
    },
    bgColor: {
      type: String,
      default: () => 'bg-secondary-1',
    },
    billingServiceCategories: {
      type: Array,
      default: () => [],
    },
    canRemove: {
      type: Boolean,
      default: () => true,
    },
  },
  data: () => ({
    amount: null,
    accountNumber: null,
    accountType: null,
    billingServiceCategory: null,
    hasValidate: true,
    hasValidationErrors: false,
    accountTypes: [
      { id: 2, name: 'Estatal' },
      { id: 1, name: 'Federal' },
    ],
  }),
  computed: {
    isFederal() {
      return (
        this.accountType?.id === accountTypeValues.Federal ||
        this.value?.figureTypeId === accountTypeValues.Federal
      );
    },
    isEstatal() {
      return (
        this.accountType?.id === accountTypeValues.Estatal ||
        this.value?.figureTypeId === accountTypeValues.Estatal
      );
    },

    filteredAccountNumbers() {
      if (!this.accountType) return [];

      const fromDate = this.dateFrom ? new Date(this.dateFrom) : null;
      const toDate = this.dateTo ? new Date(this.dateTo) : null;

      return this.accountNumbers.filter((acc) => {
        const isFederalWithCategory =
          acc.figureTypeId === accountTypeValues.Federal &&
          acc.billingServiceCategoryId === this.billingServiceCategory?.id;

        const isEstatalAccount = acc.figureTypeId === accountTypeValues.Estatal;

        const effectiveFrom = acc.effectiveDateFrom
          ? new Date(acc.effectiveDateFrom)
          : null;
        const effectiveTo = acc.effectiveDateTo
          ? new Date(acc.effectiveDateTo)
          : null;

        const isValidFromDate = fromDate ? effectiveFrom <= fromDate : true;
        const isValidToDate = toDate ? effectiveTo >= toDate : true;

        return (
          (isFederalWithCategory || isEstatalAccount) &&
          isValidFromDate &&
          isValidToDate
        );
      });
    },
  },
  watch: {
    accountType: 'resetFilters',
    billingServiceCategory: 'resetFilters',
    dateFrom: 'resetFilters',
    dateTo: 'resetFilters',
  },
  methods: {
    resetFilters() {
      if (this.isEstatal || !this.accountType) {
        this.billingServiceCategory = null;
      }
      this.accountNumber = null;
    },
    async add() {
      this.hasValidate = true;
      this.hasValidationErrors = false;
      const isValid = await this.$refs.accountNumbers.validate();
      if (!isValid) {
        this.hasValidationErrors = true;
        return;
      }

      const accountNumber = {
        ...this.accountNumber,
        ...this.billingServiceCategory,
        isFederal: this.accountType.id == accountTypeValues.Federal,
        amount: this.amount,
        id: 0,
      };

      this.$emit('add', accountNumber);

      this.accountNumber = null;
      this.amount = null;
      this.$refs.accountNumbers.reset();
    },
    remove() {
      this.$emit('remove', {
        ...this.value,
      });
    },
    cancel() {
      this.$emit('cancel');
    },
  },
};
</script>

<style scoped lang="scss">
.custom-action-button {
  ::v-deep {
    i {
      margin-right: 0 !important;
    }
  }
}
</style>
